
import { AgentTemplate } from '@/data/agentTemplates';

export type ApiProvider = 'openai' | 'gemini' | 'claude' | 'custom_url';

// Import statement and initialization based on the provider
export function getApiConfiguration(provider: ApiProvider = 'openai'): { 
  apiImport: string; 
  apiInitialization: string;
  apiModelParam: string;
} {
  let apiImport = '';
  let apiInitialization = '';
  let apiModelParam = '';
  
  switch(provider) {
    case 'openai':
      apiImport = `import OpenAI from 'openai';`;
      apiInitialization = `const openai = new OpenAI({
  apiKey: process.env.OPENAI_API_KEY, // Safely store this in .env
});`;
      apiModelParam = 'gpt-4o';
      break;
    case 'gemini':
      apiImport = `import { GoogleGenerativeAI } from '@google/generative-ai';`;
      apiInitialization = `const genAI = new GoogleGenerativeAI(process.env.GEMINI_API_KEY); // Safely store this in .env
const model = genAI.getGenerativeModel({ model: 'gemini-pro' });`;
      break;
    case 'claude':
      apiImport = `import Anthropic from '@anthropic-ai/sdk';`;
      apiInitialization = `const anthropic = new Anthropic({
  apiKey: process.env.CLAUDE_API_KEY, // Safely store this in .env
});`;
      apiModelParam = 'claude-3-opus-20240229';
      break;
    case 'custom_url':
      apiImport = `// Using custom API via fetch`;
      apiInitialization = `const API_URL = process.env.CUSTOM_API_URL; // Your custom API URL (e.g. localhost)`;
      break;
    default:
      apiImport = `import OpenAI from 'openai';`;
      apiInitialization = `const openai = new OpenAI({
  apiKey: process.env.OPENAI_API_KEY, // Safely store this in .env
});`;
      apiModelParam = 'gpt-4o';
  }
  
  return { apiImport, apiInitialization, apiModelParam };
}

// Generate provider-specific run function
export function getRunAgentFunction(provider: ApiProvider = 'openai', apiModelParam: string = ''): string {
  let runAgentFunction = '';
  
  switch(provider) {
    case 'openai':
      runAgentFunction = `const response = await openai.chat.completions.create({
    model: "${apiModelParam || 'gpt-4o'}",
    messages: [
      {
        role: "system",
        content: systemPrompt
      },
      {
        role: "user",
        content: userInput
      }
    ],
    max_tokens: 500
  });

  return response.choices[0].message.content;`;
      break;
    case 'gemini':
      runAgentFunction = `const result = await model.generateContent({
    contents: [
      {
        role: "user",
        parts: [{ text: systemPrompt + "\\n\\n" + userInput }]
      }
    ],
    generationConfig: {
      maxOutputTokens: 500,
    }
  });

  return result.response.text();`;
      break;
    case 'claude':
      runAgentFunction = `const response = await anthropic.messages.create({
    model: "${apiModelParam || 'claude-3-opus-20240229'}",
    system: systemPrompt,
    messages: [
      {
        role: "user",
        content: userInput
      }
    ],
    max_tokens: 500
  });

  return response.content[0].text;`;
      break;
    case 'custom_url':
      runAgentFunction = `const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      systemPrompt,
      userInput
    })
  });

  const data = await response.json();
  return data.response || "No response from custom API";`;
      break;
    default:
      runAgentFunction = `const response = await openai.chat.completions.create({
    model: "gpt-4o",
    messages: [
      {
        role: "system",
        content: systemPrompt
      },
      {
        role: "user",
        content: userInput
      }
    ],
    max_tokens: 500
  });

  return response.choices[0].message.content;`;
  }

  return runAgentFunction;
}
